import { useQuery } from "@apollo/client";
import a from "indefinite";
import { uniqBy } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  AssessmentType,
  ComplianceProgramState,
  GetComplianceProgramsQuery,
  GetComplianceProgramsQueryVariables,
} from "types/graphql-schema";

import getComplianceProgramsQuery from "@apps/programs/graphql/get-compliance-programs-query";
import { REVIEW_PROGRAM_BASE_PATH } from "@apps/programs/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Loading from "@components/loading/loading";
import useDebounce from "@components/use-debounce/use-debounce";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { assertEdgesNonNull, getUrlParam } from "@helpers/helpers";

import ManagerEffectivenessReport from "./manager-effectiveness-report";
import PerformanceAssessmentReport from "./performance-assessment-report";
import ReportTypeToggle from "./report-type-toggle";

const AssessmentReportPage = ({
  assessmentType,
  embedded = false,
  enableUpwardToggle = false,
}: {
  assessmentType: AssessmentType;
  // When embedded as a tab in the Reviews page (no Reporting nav bar), tighten
  // the report's top spacing.
  embedded?: boolean;
  // When true (Reviews "Results" tab), show a toggle to switch the selected
  // program between its standard (Performance) and Upward (Manager) reports.
  // The toggle only appears when the selected program has upward review data.
  enableUpwardToggle?: boolean;
}) => {
  const label = useLabel();
  const location = useLocation();
  const history = useHistory();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500);
  const programIdFromUrl = getUrlParam(location, "programId");
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(
    programIdFromUrl ? parseInt(programIdFromUrl) : null,
  );
  const [availablePrograms, setAvailablePrograms] = useState<
    ComboboxGenericOption<number>[] | null
  >(null);
  // Which report to show when the upward toggle is enabled.
  const [reportType, setReportType] = useState<AssessmentType>(assessmentType);
  // Tracks which programs have upward (manager) review data, to decide whether
  // to show the toggle for the selected program.
  const [programHasUpwardById, setProgramHasUpwardById] = useState<
    Record<number, boolean>
  >({});
  const activeType = enableUpwardToggle ? reportType : assessmentType;

  const handleChangeProgram = useCallback(
    (programId: number, overrideUrl = false) => {
      const params = new URLSearchParams(location.search);
      params.set("programId", `${programId}`);
      const url = `${location.pathname}?${params}`;
      if (overrideUrl) {
        history.replace(url);
      } else {
        history.push(url);
      }
    },
    [history, location],
  );

  const organization = currentOrganizationVar();
  const { loading: isLoadingProgramList } = useQuery<
    GetComplianceProgramsQuery,
    GetComplianceProgramsQueryVariables
  >(getComplianceProgramsQuery, {
    variables: {
      search: debouncedQuery,
      organizationId: organization.id,
      state_In: [
        ComplianceProgramState.Published,
        ComplianceProgramState.Paused,
        // A closed cycle still has results to report on; only writing is locked.
        ComplianceProgramState.Closed,
      ],
      // With the upward toggle, list programs that have either standard or
      // upward reviews; otherwise filter to the single report type.
      ...(enableUpwardToggle
        ? {
            assessmentTypes: [
              AssessmentType.Performance,
              AssessmentType.Manager,
            ],
          }
        : { assessmentType }),
    },
    onCompleted: (response) => {
      const programs = response.detailedCompliancePrograms
        ? assertEdgesNonNull(response.detailedCompliancePrograms)
        : [];
      const programOptions = programs.map(
        ({ id, title, startDate, dueDate }) => ({
          value: id,
          label: title,
          description: `${moment(startDate).format("MMM D, YYYY")} - ${moment(
            dueDate,
          ).format("MMM D, YYYY")}`,
        }),
      );
      setAvailablePrograms((prev) =>
        uniqBy([...programOptions, ...(prev ?? [])], "value"),
      );
      setProgramHasUpwardById((prev) => {
        const next = { ...prev };
        programs.forEach((program) => {
          next[program.id] = !!program.managerAssessmentTemplate;
        });
        return next;
      });
      if (!selectedProgramId) {
        if (programs.length > 0) {
          const programIds = programs.map((program) => program.id);
          const validProgramInUrl =
            selectedProgramId !== null &&
            programIds.includes(selectedProgramId);
          const programId = validProgramInUrl
            ? selectedProgramId
            : programs[0].id;
          handleChangeProgram(programId, true);
        } else {
          setSelectedProgramId(null);
        }
      }
    },
  });

  useEffect(() => {
    const programIdFromUrl = getUrlParam(location, "programId");
    if (programIdFromUrl) {
      const programId = parseInt(programIdFromUrl);
      setSelectedProgramId(programId);
    } else {
      setSelectedProgramId(null);
    }
    // only load program data when location changes
  }, [location]);
  useEffect(() => {
    // reset program dropdown when report type changes
    setAvailablePrograms([]);
    setQuery("");
  }, [assessmentType]);

  const selectedHasUpward = selectedProgramId
    ? !!programHasUpwardById[selectedProgramId]
    : false;

  useEffect(() => {
    // Fall back to the standard report if the selected program has no upward data.
    if (
      enableUpwardToggle &&
      reportType === AssessmentType.Manager &&
      !selectedHasUpward
    ) {
      setReportType(AssessmentType.Performance);
    }
  }, [enableUpwardToggle, reportType, selectedHasUpward]);

  const renderHeaderContent = (settings: JSX.Element) => (
    <div className="flex items-center gap-4">
      <div className="max-w-[200px] shrink">
        <ComboboxGeneric
          prefix={label("review", { capitalize: true })}
          options={availablePrograms ?? []}
          onChangeValue={(opt) => handleChangeProgram(opt.value)}
          value={
            (availablePrograms ?? []).find(
              ({ value }) => value === selectedProgramId,
            ) ?? null
          }
          query={query}
          setQuery={setQuery}
        />
      </div>
      {enableUpwardToggle && selectedHasUpward && (
        <ReportTypeToggle activeType={activeType} onChange={setReportType} />
      )}
      {settings}
    </div>
  );

  if (isLoadingProgramList) {
    return (
      <div className="flex-1 flex justify-center p-10">
        <Loading>Loading...</Loading>
      </div>
    );
  }

  return (
    <div className="w-full">
      {availablePrograms !== null && availablePrograms.length === 0 ? (
        <div className="px-8">
          <Callout
            icon="info"
            title="No Programs Available"
            center
            className="py-6"
          >
            There is nothing to display on this page because your{" "}
            {label("organization")} has not created any{" "}
            {label("program", { pluralize: true })} with{" "}
            {a(getAssessmentTypeLabel(assessmentType))} {label("review")}{" "}
            included.
            <Button
              text="Create Program"
              theme={buttonTheme.default}
              to={`${REVIEW_PROGRAM_BASE_PATH}/new`}
            />
          </Callout>
        </div>
      ) : (
        selectedProgramId &&
        availablePrograms &&
        (activeType === AssessmentType.Performance ? (
          <PerformanceAssessmentReport
            complianceProgramId={selectedProgramId}
            headerContent={renderHeaderContent}
            isInReporting
            embedded={embedded}
          />
        ) : (
          <ManagerEffectivenessReport
            complianceProgramId={selectedProgramId}
            headerContent={renderHeaderContent}
            isInReporting
            embedded={embedded}
          />
        ))
      )}
    </div>
  );
};

export default AssessmentReportPage;
